/**
 * EÜR — Einnahmenüberschussrechnung.
 *
 * Die Seite, die am Jahresende gebraucht wird. Sie zeigt die Zahlen je
 * EÜR-Position, damit sie in ELSTER oder beim Steuerberater eingetragen
 * werden können, und liefert den CSV-Export für ebendiesen.
 *
 * Was hier steht, ist eine Aufstellung — keine Steuererklärung und keine
 * Beratung. Ob die Buchführung ordnungsmäßig ist, entscheidet nicht das
 * Programm.
 */
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Seite } from '../bausteine/Layout';
import { Meldung } from '../bausteine/Meldung';
import { Laedt, Leer } from '../bausteine/Zustand';
import { useAbruf } from '../lib/abruf';
import { datum, jahrHeute, monatsname } from '../lib/format';
import { alsCent, euro, formatiereCent } from '../lib/geld';
import type { Euer, Grenzpruefung } from '../lib/typen';

type Art = 'jahr' | 'quartal' | 'monat';

export function Auswertung(): ReactNode {
  const [jahr, setzeJahr] = useState(jahrHeute());
  const [art, setzeArt] = useState<Art>('jahr');
  const [nummer, setzeNummer] = useState(1);

  const abfrage =
    art === 'jahr'
      ? `jahr=${jahr}&art=jahr`
      : `jahr=${jahr}&art=${art}&nummer=${nummer}`;

  const euer = useAbruf<Euer>(`/api/auswertung/euer?${abfrage}`);
  const grenzen = useAbruf<Grenzpruefung>(`/api/auswertung/grenzen?jahr=${jahr}`);

  const jahre = [jahrHeute(), jahrHeute() - 1, jahrHeute() - 2, jahrHeute() - 3];

  return (
    <Seite
      titel="EÜR"
      untertitel={
        euer.daten
          ? `${datum(euer.daten.zeitraum.von)} bis ${datum(euer.daten.zeitraum.bis)}`
          : undefined
      }
      aktionen={
        <>
          <select
            value={jahr}
            onChange={(e) => setzeJahr(Number.parseInt(e.target.value, 10))}
            aria-label="Jahr"
            style={{ width: 'auto' }}
          >
            {jahre.map((j) => (
              <option key={j} value={j}>
                {j}
              </option>
            ))}
          </select>

          <select
            value={art}
            onChange={(e) => setzeArt(e.target.value as Art)}
            aria-label="Zeitraum"
            style={{ width: 'auto' }}
          >
            <option value="jahr">Jahr</option>
            <option value="quartal">Quartal</option>
            <option value="monat">Monat</option>
          </select>

          {art !== 'jahr' ? (
            <select
              value={nummer}
              onChange={(e) => setzeNummer(Number.parseInt(e.target.value, 10))}
              aria-label={art === 'quartal' ? 'Quartal' : 'Monat'}
              style={{ width: 'auto' }}
            >
              {(art === 'quartal' ? [1, 2, 3, 4] : Array.from({ length: 12 }, (_, i) => i + 1)).map(
                (n) => (
                  <option key={n} value={n}>
                    {art === 'quartal' ? `${n}. Quartal` : monatsname(n)}
                  </option>
                ),
              )}
            </select>
          ) : null}

          {/* Ein echter Verweis, kein fetch(): Der Browser lädt die Datei
              mit dem Namen aus dem Content-Disposition-Kopf herunter. */}
          <a className="knopf" href={`/api/auswertung/export?jahr=${jahr}&format=csv`}>
            CSV für den Steuerberater
          </a>
        </>
      }
    >
      <div className="stapel">
        {euer.fehler ? <Meldung ton="gefahr">{euer.fehler}</Meldung> : null}

        {grenzen.daten?.meldung ? (
          <Meldung
            ton={grenzen.daten.status === 'ueberschritten' ? 'gefahr' : 'warnung'}
            titel="§ 19 UStG"
          >
            {grenzen.daten.meldung}
          </Meldung>
        ) : null}

        {euer.daten && euer.daten.hinweise.ohneKategorie > 0 ? (
          <Meldung ton="warnung" titel={`${euer.daten.hinweise.ohneKategorie} Belege ohne Kategorie.`}>
            Sie erscheinen in keiner Position unten und fehlen damit in der
            Aufstellung. <Link to="/belege">Nachtragen</Link>.
          </Meldung>
        ) : null}

        {!euer.daten ? (
          <div className="karte">
            <Laedt was="Auswertung wird gerechnet" />
          </div>
        ) : (
          <>
            <div className="raster drei">
              <div className="karte kennzahl">
                <div className="beschriftung">Betriebseinnahmen</div>
                <div className="wert">{euer.daten.anzeige.einnahmen}</div>
              </div>
              <div className="karte kennzahl">
                <div className="beschriftung">Betriebsausgaben</div>
                <div className="wert">{euer.daten.anzeige.ausgaben}</div>
              </div>
              <div
                className={`karte kennzahl ${alsCent(euer.daten.ergebnis) >= 0 ? 'gut' : 'gefahr'}`}
              >
                <div className="beschriftung">
                  {alsCent(euer.daten.ergebnis) >= 0 ? 'Gewinn' : 'Verlust'}
                </div>
                <div className="wert">{euer.daten.anzeige.ergebnis}</div>
              </div>
            </div>

            <Positionstabelle euer={euer.daten} />

            {euer.daten.hinweise.offenePosten.anzahl > 0 ? (
              <Meldung ton="info" titel={`${euer.daten.hinweise.offenePosten.anzahl} offene Posten.`}>
                {euro(euer.daten.hinweise.offenePosten.einnahmen)} noch zu
                erhalten, {euro(euer.daten.hinweise.offenePosten.ausgaben)} noch
                zu zahlen. Beides zählt erst im Jahr der Zahlung —
                Zuflussprinzip.
              </Meldung>
            ) : null}

            <div className="karte">
              <div className="karte-koerper klein still">
                <p>
                  Gezählt wird nach Zahlungsdatum. Enthalten sind gebuchte und
                  festgeschriebene Belege; Entwürfe, Stornos, private und
                  neutrale Kategorien bleiben außen vor. Privatanteile und
                  Abzugsbeschränkungen — etwa 70 % bei Bewirtung — sind je
                  Beleg berücksichtigt und gerundet.
                </p>
                <p style={{ marginBottom: 0 }}>
                  Diese Aufstellung ersetzt keine Steuerberatung. Die
                  Zeilennummern der Anlage EÜR ändern sich fast jedes Jahr und
                  sind hier nur ausgewiesen, wenn sie für das Jahr hinterlegt
                  wurden.
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    </Seite>
  );
}

function Positionstabelle({ euer }: { euer: Euer }): ReactNode {
  if (euer.positionen.length === 0) {
    return (
      <div className="karte">
        <Leer
          titel="Keine gebuchten Belege in diesem Zeitraum"
          hinweis="Erfasste Belege ohne Zahlungsdatum zählen noch nicht."
        />
      </div>
    );
  }

  const bereiche = [
    { titel: 'Betriebseinnahmen', richtung: 'einnahme' as const },
    { titel: 'Betriebsausgaben', richtung: 'ausgabe' as const },
  ];

  return (
    <>
      {bereiche.map((bereich) => {
        const zeilen = euer.positionen.filter((p) => p.richtung === bereich.richtung);
        if (zeilen.length === 0) return null;
        const summe = zeilen.reduce((s, p) => s + alsCent(p.betrag), 0);

        return (
          <div className="karte" key={bereich.richtung}>
            <div className="karte-kopf">
              <h2>{bereich.titel}</h2>
              <span className="zahl">{formatiereCent(summe)}</span>
            </div>
            <div className="tabelle-rahmen">
              <table className="tabelle">
                <thead>
                  <tr>
                    <th className="schmal">Zeile</th>
                    <th>Position</th>
                    <th className="zahl schmal">Belege</th>
                    <th className="zahl">Betrag</th>
                  </tr>
                </thead>
                <tbody>
                  {zeilen.map((p) => (
                    <tr key={p.position ?? p.bezeichnung}>
                      <td className="schmal still">
                        {/* 0 heißt „für dieses Jahr noch nicht zugeordnet"
                            (002-stammdaten.sql). Als Zahl angezeigt sähe es
                            wie eine echte Zeilennummer aus. */}
                        {p.zeile && p.zeile > 0 ? p.zeile : '—'}
                      </td>
                      <td>
                        <span className="haupttext">{p.bezeichnung}</span>
                        <div className="nebentext">
                          {p.kategorien
                            .map((k) => `${k.bezeichnung} (${euro(k.betrag, false)})`)
                            .join(' · ')}
                        </div>
                      </td>
                      <td className="zahl schmal still">{p.anzahl}</td>
                      <td className="zahl">{euro(p.betrag, false)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={3}>Summe {bereich.titel}</td>
                    <td className="zahl">{formatiereCent(summe, false)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        );
      })}
    </>
  );
}
